import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ShoppingBag, Heart, Eye, CreditCard, Truck, CheckCircle, Headphones, ArrowRight, Sparkles } from 'lucide-react';
import { useProductsContext } from '../../context/products_context';
import { useLanguageContext } from '../../context/language_context';
import SEOHead from '../../components/SEOHead/';
import { ProductListJsonLd } from '../../components/SEOHead/JsonLd';
import { PAGE_SEO } from '../../utils/seo';
import { useUserContext } from '../../context/user_context';
import { useFilterContext } from '../../context/filter_context';
import { useCartContext } from '../../context/cart_context';
import { useWishlistContext } from '../../context/wishlist_context';
import { formatPrice, getSecureUrl } from '../../utils/helpers';
import { categories_url, hero_images_url, platform_reviews_url } from '../../utils/constants';
import Loading from '../../components/Loading';
import Error from '../../components/Error';

// Import assets
import heroLiving from '../../assets/hero-living-room.jpg';
import heroKitchen from '../../assets/hero-kitchen.jpg';
import heroBedroom from '../../assets/hero-bedroom.jpg';
import lifestyleCozy from '../../assets/lifestyle-cozy.jpg';
import roomKitchen from '../../assets/room-kitchen.jpg';
import roomLiving from '../../assets/room-living.jpg';
import roomBedroom from '../../assets/room-bedroom.jpg';
import roomBathroom from '../../assets/room-bathroom.jpg';
import roomStorage from '../../assets/room-storage.jpg';

const defaultSlides = [
  {
    image: heroLiving,
    title: 'Darista',
    titleAr: 'داريستا',
    subtitle: 'Smart solutions for your home',
    subtitleAr: 'حلول ذكية لمنزلك',
    description: 'Everything to make your daily life easier',
    descriptionAr: 'كل ما يجعل حياتك اليومية أسهل',
  },
  {
    image: heroKitchen,
    title: 'Kitchen Essentials',
    titleAr: 'أساسيات المطبخ',
    subtitle: 'Cook with joy',
    subtitleAr: 'اطبخ بفرح',
    description: 'Premium tools for every meal',
    descriptionAr: 'أدوات فاخرة لكل وجبة',
  },
  {
    image: heroBedroom,
    title: 'Restful Retreats',
    titleAr: 'ملاذات مريحة',
    subtitle: 'Design your sanctuary',
    subtitleAr: 'صمم ملاذك',
    description: 'Where comfort meets elegance',
    descriptionAr: 'حيث تلتقي الراحة بالأناقة',
  },
];

const defaultRoomImages = {
  kitchen: roomKitchen,
  living: roomLiving,
  bedroom: roomBedroom,
  bathroom: roomBathroom,
  storage: roomStorage,
};

const roomImagesList = [roomKitchen, roomLiving, roomBedroom, roomBathroom, roomStorage];

const trustFeatures = [
  { icon: CreditCard, title: 'Cash on Delivery', titleAr: 'الدفع عند الاستلام', desc: 'Pay when you receive your order', descAr: 'ادفع عند استلام طلبك', color: 'olive' },
  { icon: Truck, title: 'Fast Delivery', titleAr: 'توصيل سريع', desc: 'Quick shipping across Tunisia', descAr: 'شحن سريع في جميع أنحاء تونس', color: 'gold' },
  { icon: CheckCircle, title: 'Quality Checked', titleAr: 'جودة مضمونة', desc: 'Every product carefully inspected', descAr: 'كل منتج يتم فحصه بعناية', color: 'olive' },
  { icon: Headphones, title: 'Customer Support', titleAr: 'دعم العملاء', desc: 'Dedicated support in Tunisia', descAr: 'دعم مخصص في تونس', color: 'gold' },
];

const HomePage = () => {
  const { t, isArabic } = useLanguageContext();
  const { currentUser } = useUserContext();
  const { setCategoryFilter } = useFilterContext();
  const { addToCart } = useCartContext();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlistContext();
  const history = useHistory();
  const {
    products_loading: loading,
    products_error: error,
    featured_products: featured,
    products,
  } = useProductsContext();

  const [categories, setCategories] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [platformReviews, setPlatformReviews] = useState([]);
  const slideInterval = useRef(null);

  // Touch swipe state for hero carousel
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  // Categories scroll tracking for dots
  const [activeCategoryDot, setActiveCategoryDot] = useState(0);
  const categoriesGridRef = useRef(null);
  const categoryDotsCount = 5;

  // Review Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const fetchPlatformReviews = async () => {
    try {
      const { data } = await axios.get(platform_reviews_url);
      if (data.success) {
        const reviews = (data.reviews || []).map((r) => {
          const sanitized = { ...r };
          Object.keys(sanitized).forEach((k) => {
            if (typeof sanitized[k] === 'string' && sanitized[k].startsWith('http://')) {
              sanitized[k] = getSecureUrl(sanitized[k]);
            }
          });
          return sanitized;
        });
        setPlatformReviews(reviews);
      }
    } catch (err) { /* silent */ }
  };

  const handleAddReviewClick = () => {
    if (!currentUser) {
      history.push('/login');
    } else {
      setShowReviewModal(true);
    }
  };

  const handleReviewSubmit = async () => {
    if (reviewStars === 0) {
      toast.error(isArabic ? 'يرجى تحديد تقييم' : 'Please select a rating');
      return;
    }
    if (!reviewComment.trim()) {
      toast.error(isArabic ? 'يرجى كتابة تعليق' : 'Please write a comment');
      return;
    }
    setReviewLoading(true);
    try {
      const { data } = await axios.post(platform_reviews_url, {
        name: currentUser.displayName || 'User',
        email: currentUser.email,
        rating: reviewStars,
        comment: reviewComment,
      });
      if (data.success) {
        toast.success(isArabic ? 'تم إضافة تقييمك بنجاح!' : 'Your review has been added successfully!');
        setShowReviewModal(false);
        setReviewStars(0);
        setReviewComment('');
        fetchPlatformReviews();
      } else {
        toast.error(data.message || (isArabic ? 'حدث خطأ' : 'An error occurred'));
      }
    } catch (err) {
      toast.error(isArabic ? 'حدث خطأ أثناء إرسال التقييم' : 'Error submitting review');
    }
    setReviewLoading(false);
  };

  useEffect(() => {
    document.title = 'Darista | Home';
    fetchCategories();
    fetchHeroImages();
    fetchPlatformReviews();
  }, []);

  // Auto-slide carousel
  useEffect(() => {
    const slides = heroImages.length > 0 ? heroImages : defaultSlides;
    if (slides.length > 1) {
      slideInterval.current = setInterval(() => {
        setSlideDirection(1);
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 7000);
    }
    return () => { if (slideInterval.current) clearInterval(slideInterval.current); };
  }, [heroImages.length]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(categories_url);
      const cats = (response.data.data || []).map((cat) => ({
        ...cat,
        image: cat.image ? { ...cat.image, url: getSecureUrl(cat.image.url) } : cat.image,
      }));
      setCategories(cats);
    } catch (err) { /* silent */ }
  };

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(hero_images_url);
      const imgs = (response.data.data || []).map((h) => ({
        ...h,
        image: h.image ? { ...h.image, url: getSecureUrl(h.image.url) } : h.image,
      }));
      setHeroImages(imgs);
    } catch (err) { /* silent */ }
  };

  const goToSlide = (index) => {
    const slides = heroImages.length > 0 ? heroImages : defaultSlides;
    setSlideDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
      slideInterval.current = setInterval(() => {
        setSlideDirection(1);
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 7000);
    }
  };

  const goPrev = () => {
    const slides = heroImages.length > 0 ? heroImages : defaultSlides;
    setSlideDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goNext = () => {
    const slides = heroImages.length > 0 ? heroImages : defaultSlides;
    setSlideDirection(1);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  // Touch swipe handlers for hero carousel
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) goNext();
    if (isRightSwipe) goPrev();
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (loading) return <Loading />;
  if (error) return <Error />;

  const slides = heroImages.length > 0 ? heroImages : defaultSlides;
  const currentHero = slides[currentSlide] || slides[0];
  const heroImageUrl = heroImages.length > 0 ? getSecureUrl(currentHero.image?.url || currentHero.image) : currentHero.image;

  // Categories display
  const displayCategories = categories.length > 0
    ? categories
    : [...new Set(products.map(p => p.category))].filter(Boolean).slice(0, 5).map((cat, index) => ({
      _id: index,
      name: cat,
      image: { url: products.find(p => p.category === cat)?.images?.[0]?.url || '' }
    }));

  const handleCategoryClick = (categoryName) => {
    // Convert category name to URL-friendly format
    const categorySlug = categoryName.toLowerCase().replace(/\s+/g, '-');
    history.push(`/products?category=${categorySlug}`);
  };

  // Best Sellers - Add to Cart handler
  const handleBSAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const { id, name, price, images, image, colors, sizes, variants, stock, shipping } = product;
    const imageUrl = image || images?.[0]?.url || images?.[0] || '';
    // Build default variant selections from product variants
    const defaultVariantSelections = {};
    if (variants && variants.length > 0) {
      variants.forEach((v) => {
        if (v.values && v.values.length > 0) {
          defaultVariantSelections[v.name] = v.values[0];
        }
      });
    }
    const defaultColor = colors?.[0] || '';
    const defaultSize = sizes?.[0] || '';
    addToCart(id, defaultColor, defaultSize, 1, {
      id,
      name,
      price,
      image: imageUrl,
      colors: colors || [],
      sizes: sizes || [],
      variants: variants || [],
      shipping,
      stock: stock || 10,
    }, Object.keys(defaultVariantSelections).length > 0 ? defaultVariantSelections : undefined);
    toast.success(isArabic ? '✓ تمت الإضافة للسلة' : '✓ Added to cart', {
      position: 'top-right',
      autoClose: 2000,
      hideProgressBar: true,
    });
  };

  // Best Sellers - Wishlist toggle handler
  const handleBSToggleWishlist = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const { id, name, price, images, image, colors, sizes, stock, category, shipping } = product;
    const imageUrl = image || images?.[0]?.url || images?.[0] || '';
    if (isInWishlist(id)) {
      removeFromWishlist(id);
      toast.info(isArabic ? 'تمت الإزالة من المفضلة' : 'Removed from wishlist', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
      });
    } else {
      addToWishlist({
        _id: id,
        id,
        name,
        price,
        image: imageUrl,
        colors: colors || ['#000000'],
        sizes: sizes || [],
        shipping,
        stock: stock || 10,
        category,
      });
      toast.success(isArabic ? '♥ تمت الإضافة للمفضلة' : '♥ Added to wishlist', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  // Best Sellers - Quick view handler
  const handleBSQuickView = (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    history.push(`/products/${productId}`);
  };

  const seo = isArabic ? PAGE_SEO.home.ar : PAGE_SEO.home.en;

  return (
    <Wrapper dir={isArabic ? 'rtl' : 'ltr'}>
      <SEOHead
        title={seo.title}
        description={seo.description}
        path='/'
        lang={isArabic ? 'ar' : 'en'}
      />
      <ProductListJsonLd products={featured} listName='Featured Products' />

      {/* =================== HERO CAROUSEL =================== */}
      <section
        className="hero-section"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence initial={false} custom={slideDirection}>
          <motion.div
            key={currentSlide}
            custom={slideDirection}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.8 }}
            className="hero-slide"
          >
            <motion.img
              src={heroImageUrl}
              alt={currentHero.title || ''}
              className="hero-bg-image"
              initial={{ scale: 1 }}
              animate={{ scale: 1.08 }}
              transition={{ duration: 8, ease: 'linear' }}
            />
            <div className="hero-gradient-overlay" />
          </motion.div>
        </AnimatePresence>

        {/* Floating particles */}
        <div className="hero-particles">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              style={{ left: `${15 + i * 15}%`, top: `${20 + (i % 3) * 25}%` }}
              animate={{ y: [0, -30, 0], opacity: [0.2, 0.5, 0.2] }}
              transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.5 }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="hero-content">
          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="hero-text-wrapper"
            >
              <motion.div
                className="hero-decorative-line"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <span className="line-bar" />
                <span className="line-text">{isArabic ? 'مرحبًا بكم في' : 'Welcome to'}</span>
                <span className="line-bar" />
              </motion.div>

              <motion.h1
                className="hero-title"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                {heroImages.length > 0
                  ? (isArabic ? currentHero.titleAr || currentHero.title : currentHero.title)
                  : (isArabic ? currentHero.titleAr : currentHero.title)}
              </motion.h1>

              <motion.p
                className="hero-subtitle"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.7 }}
              >
                {heroImages.length > 0
                  ? (isArabic ? currentHero.subtitleAr || currentHero.subtitle : currentHero.subtitle)
                  : (isArabic ? currentHero.subtitleAr : currentHero.subtitle)}
              </motion.p>

              <motion.p
                className="hero-description"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                {heroImages.length > 0
                  ? (isArabic ? currentHero.descriptionAr || '' : currentHero.description || '')
                  : (isArabic ? currentHero.descriptionAr : currentHero.description)}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                <Link to="/products" className="hero-cta-btn">
                  <span>{isArabic ? 'اكتشف المجموعة' : 'Discover Collection'}</span>
                  <motion.span animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>→</motion.span>
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Nav Arrows */}
        {slides.length > 1 && (
          <>
            <button className="hero-arrow hero-arrow-left" onClick={goPrev} aria-label="Previous"><ChevronLeft size={32} /></button>
            <button className="hero-arrow hero-arrow-right" onClick={goNext} aria-label="Next"><ChevronRight size={32} /></button>
          </>
        )}

        {/* Dots */}
        {slides.length > 1 && (
          <div className="hero-dots">
            {slides.map((_, index) => (
              <button key={index} className={`hero-dot ${index === currentSlide ? 'active' : ''}`} onClick={() => goToSlide(index)} aria-label={`Slide ${index + 1}`} />
            ))}
          </div>
        )}

        {/* Scroll indicator */}
        <motion.div
          className="scroll-indicator"
          animate={{ x: '-50%', y: [0, 10, 0] }}
          transition={{ y: { duration: 2, repeat: Infinity }, x: { duration: 0 } }}
        >
          <span>{isArabic ? 'تمرير' : 'Scroll'}</span>
          <div className="scroll-mouse">
            <motion.div className="scroll-dot" animate={{ y: [0, 16, 0] }} transition={{ duration: 1.5, repeat: Infinity }} />
          </div>
        </motion.div>
      </section>

      {/* =================== SHOP BY CATEGORY =================== */}
      {displayCategories.length > 0 && (
        <section className="categories-section">
          <div className="section-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="section-label">{isArabic ? 'استكشف المساحات' : 'Explore Spaces'}</span>
              <h2 className="section-title-main">{isArabic ? 'تسوق حسب الفئة' : 'Shop by Category'}</h2>
              <div className="section-divider" />
              <p className="section-subtitle">{isArabic ? 'اعثر على القطع المثالية لكل ركن في منزلك' : 'Find the perfect pieces for every corner of your home'}</p>
            </motion.div>

            <div
              className="categories-grid"
              ref={categoriesGridRef}
              onScroll={(e) => {
                const { scrollLeft, scrollWidth, clientWidth } = e.target;
                const maxScroll = scrollWidth - clientWidth;
                if (maxScroll > 0) {
                  const progress = scrollLeft / maxScroll;
                  const newDot = Math.min(categoryDotsCount - 1, Math.round(progress * (categoryDotsCount - 1)));
                  setActiveCategoryDot(newDot);
                }
              }}
            >
              {displayCategories.slice(0, 5).map((category, index) => {
                const catImage = getSecureUrl(category.image?.url) || roomImagesList[index % roomImagesList.length];
                return (
                  <motion.div
                    key={category._id || index}
                    className="category-card"
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.12, type: 'spring', stiffness: 100 }}
                    whileHover={{ y: -10 }}
                    onClick={() => handleCategoryClick(category.name)}
                  >
                    <div className="category-image-wrap">
                      <img src={catImage} alt={category.name} className="category-img" />
                    </div>
                    <div className="category-gradient" />
                    <div className="category-content">
                      <h3 className="category-name">{category.name}</h3>
                      <span className="category-explore">
                        {isArabic ? 'استكشف' : 'Explore'} →
                      </span>
                    </div>
                    <div className="category-corner" />
                  </motion.div>
                );
              })}
            </div>

            {/* Category pagination dots - mobile only */}
            <div className="category-dots">
              {[...Array(categoryDotsCount)].map((_, idx) => (
                <button
                  key={idx}
                  className={`category-dot ${activeCategoryDot === idx ? 'active' : ''}`}
                  onClick={() => {
                    if (categoriesGridRef.current) {
                      const scrollWidth = categoriesGridRef.current.scrollWidth - categoriesGridRef.current.clientWidth;
                      const scrollPos = (scrollWidth / (categoryDotsCount - 1)) * idx;
                      categoriesGridRef.current.scrollTo({ left: scrollPos, behavior: 'smooth' });
                    }
                  }}
                  aria-label={`Category group ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* =================== BEST SELLERS / FEATURED =================== */}
      <section className="bestsellers-section">
        <div className="bestsellers-bg-pattern" />
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label">{isArabic ? 'المفضلة لدى العملاء' : 'Customer Favorites'}</span>
            <h2 className="section-title-main">{isArabic ? 'الأكثر مبيعًا' : 'Best Sellers'}</h2>
            <div className="section-divider" />
            <p className="section-subtitle">{isArabic ? 'اختيارات عملائنا المفضلة للمنزل العصري' : "Our customers' favorite picks for the modern home"}</p>
          </motion.div>

          <div className="products-grid">
            {featured.slice(0, 4).map((product, index) => {
              const { id, name, price, images, image } = product;
              const imageUrl = getSecureUrl(image || images?.[0]?.url || images?.[0] || '');
              return (
                <motion.div
                  key={id}
                  className="product-card-wrap"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                >
                  <div className="product-card">
                    <div className="product-image-container" onClick={(e) => handleBSAddToCart(e, product)} style={{ cursor: 'pointer' }}>
                      <img src={imageUrl} alt={name} className="product-image" referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = 'none'; }} />
                      <div className="product-hover-overlay" />
                      <span className="product-badge">{isArabic ? 'الأكثر مبيعًا' : 'Best Seller'}</span>
                      <div className="product-actions">
                        <button className="product-action-btn" onClick={(e) => handleBSToggleWishlist(e, product)} title={isInWishlist(id) ? (isArabic ? 'إزالة من المفضلة' : 'Remove from wishlist') : (isArabic ? 'إضافة للمفضلة' : 'Add to wishlist')}>
                          <Heart size={16} fill={isInWishlist(id) ? '#ef4444' : 'none'} stroke={isInWishlist(id) ? '#ef4444' : 'currentColor'} />
                        </button>
                        <button className="product-action-btn" onClick={(e) => handleBSQuickView(e, id)} title={isArabic ? 'عرض سريع' : 'Quick view'}>
                          <Eye size={16} />
                        </button>
                      </div>
                      <div className="product-add-to-cart">
                        <ShoppingBag size={16} />
                        <span>{isArabic ? 'أضف للسلة' : 'Add to Cart'}</span>
                      </div>
                    </div>
                    <div className="product-info">
                      <div className="product-rating">
                        {product.numberOfReviews > 0 ? (
                          <>
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className={`star ${i < Math.round(product.rating || 0) ? 'filled' : ''}`}>★</span>
                            ))}
                            <span className="rating-num">({product.rating})</span>
                          </>
                        ) : (
                          <span className="rating-placeholder">&nbsp;</span>
                        )}
                      </div>
                      <h3 className="product-name">{name}</h3>
                      <p className="product-price">{formatPrice(price)}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="products-cta"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <Link to="/products" className="view-all-btn">
              {isArabic ? 'عرض جميع المنتجات' : 'View All Products'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* =================== WHY CHOOSE DARISTA =================== */}
      <section className="trust-section">
        <div className="trust-bg-circle trust-bg-1" />
        <div className="trust-bg-circle trust-bg-2" />
        <div className="section-container">
          <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h3 className="trust-title">{isArabic ? 'لماذا تختار داريستا؟' : 'Why Choose Darista?'}</h3>
            <p className="trust-subtitle">{isArabic ? 'موثوق من آلاف المنازل السعيدة' : 'Trusted by thousands of happy homes'}</p>
          </motion.div>
          <div className="trust-grid">
            {trustFeatures.map((feature, index) => {
              const IconComp = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="trust-card"
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, type: 'spring', stiffness: 100 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  <div className={`trust-icon-wrap ${feature.color}`}>
                    <IconComp size={28} />
                  </div>
                  <h3 className="trust-feature-title">{isArabic ? feature.titleAr : feature.title}</h3>
                  <p className="trust-feature-desc">{isArabic ? feature.descAr : feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =================== LIMITED TIME OFFER =================== */}
      <section className="promo-section">
        <div className="promo-gradient-bg" />
        <div className="promo-shapes">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="promo-particle"
              style={{ left: `${10 + (i * 8)}%`, top: `${20 + (i % 4) * 20}%` }}
              animate={{ y: [0, -40, 0], opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }}
            />
          ))}
        </div>
        <div className="section-container promo-content">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="promo-badge">
              <Sparkles size={16} />
              <span>{isArabic ? 'عرض لفترة محدودة' : 'Limited Time Offer'}</span>
            </div>
            <h2 className="promo-title">
              {isArabic ? 'عروض' : 'Offres'} <span className="promo-highlight">{isArabic ? 'داريستا' : 'Darista'}</span>
            </h2>
            <p className="promo-desc">
              {isArabic
                ? 'اكتشف مجموعتنا الحصرية بأسعار خاصة. أساسيات منزلية عالية الجودة لحياتك اليومية.'
                : 'Discover our exclusive collection at special prices. Quality home essentials for your everyday life.'}
            </p>
            <div className="promo-buttons">
              <Link to="/offers" className="promo-btn-primary">
                {isArabic ? 'تسوق العروض' : 'Shop Offers'}
                <motion.span animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                  <ArrowRight size={20} />
                </motion.span>
              </Link>
              <Link to="/offers" className="promo-btn-outline">
                {isArabic ? 'عرض جميع الصفقات' : 'View All Deals'}
              </Link>
            </div>
            <div className="promo-stats">
              {[
                { value: '25%', label: isArabic ? 'توفير' : 'Savings' },
                { value: '100+', label: isArabic ? 'منتجات' : 'Products' },
                { value: '48h', label: isArabic ? 'متبقي' : 'Left' },
              ].map((stat) => (
                <div key={stat.label} className="promo-stat">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* =================== LIFESTYLE INSPIRATION =================== */}
      <section className="lifestyle-section">
        <div className="section-container">
          <div className="lifestyle-grid">
            <motion.div
              className="lifestyle-image-wrap"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="lifestyle-img-container">
                <img src={lifestyleCozy} alt="Lifestyle" className="lifestyle-img" />
              </div>
              <motion.div
                className="lifestyle-float-card"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <span className="float-card-number">10k+</span>
                <p className="float-card-text">{isArabic ? 'عملاء سعداء في تونس' : 'Happy customers across Tunisia'}</p>
              </motion.div>
              <div className="lifestyle-frame" />
            </motion.div>

            <div className="lifestyle-content">
              <motion.span className="section-label" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                {isArabic ? 'أسلوب الحياة' : 'Lifestyle'}
              </motion.span>
              <motion.h2
                className="lifestyle-title"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {isArabic ? (
                  <>اجعل منزلك <span className="text-olive">أذكى</span>، أكثر <span className="text-gold">جمالاً</span>، وأسهل للعيش فيه</>
                ) : (
                  <>Make your home <span className="text-olive">smarter</span>, more <span className="text-gold">beautiful</span>, and easier to live in</>
                )}
              </motion.h2>
              <motion.p className="lifestyle-desc" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                {isArabic
                  ? 'في داريستا، نؤمن أن كل منزل يستحق تصميمًا مدروسًا. مجموعاتنا المنتقاة تجمع بين الوظيفة والجمال.'
                  : 'At Darista, we believe that every home deserves thoughtful design. Our curated collections bring together function and beauty.'}
              </motion.p>
              <div className="lifestyle-features">
                {[
                  isArabic ? 'تصميم مدروس' : 'Thoughtfully designed',
                  isArabic ? 'مواد عالية الجودة' : 'Quality materials',
                  isArabic ? 'خيارات مستدامة' : 'Sustainable choices',
                ].map((feat, i) => (
                  <motion.div
                    key={feat}
                    className="lifestyle-feature-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                  >
                    <div className="feature-line" />
                    <p>{feat}</p>
                  </motion.div>
                ))}
              </div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.6 }}>
                <Link to="/about" className="lifestyle-cta">
                  {isArabic ? 'اكتشف قصتنا' : 'Explore Our Story'}
                  <motion.span animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                    <ArrowRight size={20} />
                  </motion.span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* =================== REVIEWS / GET INSPIRED =================== */}
      <section className="reviews-section">
        <div className="section-container">
          <motion.div className="section-header" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="section-label">{isArabic ? 'شهادات' : 'Testimonials'}</span>
            <h2 className="section-title-main">{isArabic ? 'آراء العملاء' : 'Customer Reviews'}</h2>
            <div className="section-divider" />
          </motion.div>

          {platformReviews.length > 0 ? (
            <>
              <div className="reviews-grid">
                {platformReviews.slice(0, 3).map((review, index) => (
                  <motion.div
                    key={review._id || index}
                    className="review-card"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="review-header">
                      <div className="review-avatar">{review.name.charAt(0).toUpperCase()}</div>
                      <div className="review-info">
                        <h4 className="review-name">{review.name}</h4>
                        <div className="review-stars">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className={i < review.rating ? 'star filled' : 'star'}>★</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="review-comment">"{review.comment}"</p>
                  </motion.div>
                ))}
              </div>
              <div className="add-review-container">
                <button className="add-review-btn" onClick={handleAddReviewClick}>
                  {isArabic ? 'أضف تقييمك' : 'Add Your Review'}
                </button>
              </div>
            </>
          ) : (
            <div className="no-reviews">
              <p>{isArabic ? 'لا توجد تقييمات حتى الآن. كن أول من يشاركنا رأيه!' : 'No reviews yet. Be the first to share your experience!'}</p>
              <button className="add-review-btn" onClick={handleAddReviewClick}>
                {isArabic ? 'أضف تقييم' : 'Add Review'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* =================== REVIEW MODAL =================== */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()} style={{ direction: isArabic ? 'rtl' : 'ltr' }}>
            <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            <h3 className="modal-title">{isArabic ? 'أضف تقييمك' : 'Add Your Review'}</h3>
            <div className="modal-field">
              <label>{isArabic ? 'التقييم' : 'Rating'}</label>
              <div className="modal-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button key={star} type="button" className={`star-btn ${star <= reviewStars ? 'active' : ''}`} onClick={() => setReviewStars(star)}>★</button>
                ))}
              </div>
            </div>
            <div className="modal-field">
              <label>{isArabic ? 'تعليقك' : 'Your Comment'}</label>
              <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder={isArabic ? 'شاركنا تجربتك...' : 'Share your experience...'} className="modal-textarea" rows={4} />
            </div>
            <div className="modal-actions">
              <button className="modal-submit" onClick={handleReviewSubmit} disabled={reviewLoading}>
                {reviewLoading ? (isArabic ? 'جاري الإرسال...' : 'Submitting...') : (isArabic ? 'إرسال التقييم' : 'Submit Review')}
              </button>
              <button className="modal-cancel" onClick={() => setShowReviewModal(false)}>{isArabic ? 'إلغاء' : 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default HomePage;

const Wrapper = styled.div`
  overflow-x: hidden;

  /* Animation for scroll indicator */
  @keyframes scrollMove {
    0% { transform: translateX(-50%) translateY(0); opacity: 1; }
    50% { transform: translateX(-50%) translateY(8px); opacity: 0; }
    51% { transform: translateX(-50%) translateY(-6px); opacity: 0; }
    100% { transform: translateX(-50%) translateY(0); opacity: 1; }
  }

  /* ===== SHARED SECTION STYLES ===== */
  .section-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 2rem;
  }
  .section-header {
    text-align: center;
    margin-bottom: 3.5rem;
  }
  .section-label {
    display: inline-block;
    font-family: var(--font-display);
    font-size: 0.95rem;
    color: var(--clr-olive);
    text-transform: uppercase;
    letter-spacing: 3px;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }
  .section-title-main {
    font-family: var(--font-display);
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    color: var(--clr-charcoal);
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  .section-divider {
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, var(--clr-olive), var(--clr-gold));
    margin: 0 auto 1.25rem;
    border-radius: 2px;
  }
  .section-subtitle {
    font-family: var(--font-body);
    font-size: 1.15rem;
    color: var(--clr-charcoal-light);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.7;
  }

  /* ===== HERO CAROUSEL ===== */
  .hero-section {
    position: relative;
    width: 100%;
    height: calc(100vh + 88px);
    height: calc(100svh + 88px);
    min-height: 550px;
    overflow: hidden;
    margin-top: -88px;
    touch-action: pan-y;
  }
  @media (min-width: 768px) {
    .hero-section {
      height: calc(100vh + 116px);
      min-height: 600px;
      margin-top: -116px;
    }
  }
  .hero-slide {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  .hero-bg-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .hero-gradient-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0,0,0,0.45) 0%,
      rgba(0,0,0,0.2) 40%,
      rgba(0,0,0,0.5) 100%
    );
  }
  .hero-particles {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
    .particle {
      position: absolute;
      width: 6px;
      height: 6px;
      background: rgba(255,255,255,0.4);
      border-radius: 50%;
    }
  }
  .hero-content {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .hero-text-wrapper {
    text-align: center;
    max-width: 800px;
    padding: 0 2rem;
  }
  .hero-decorative-line {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    .line-bar {
      width: 50px;
      height: 1px;
      background: rgba(255,255,255,0.5);
    }
    .line-text {
      font-family: var(--font-body);
      font-size: 0.85rem;
      color: rgba(255,255,255,0.8);
      text-transform: uppercase;
      letter-spacing: 4px;
    }
  }
  .hero-title {
    font-family: var(--font-display);
    font-size: clamp(3.5rem, 8vw, 6.5rem);
    color: #fff;
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    text-shadow: 0 2px 20px rgba(0,0,0,0.3);
  }
  .hero-subtitle {
    font-family: var(--font-display);
    font-size: clamp(1.4rem, 3vw, 2rem);
    color: rgba(255,255,255,0.9);
    font-weight: 400;
    margin-bottom: 1rem;
    font-style: italic;
  }
  .hero-description {
    font-family: var(--font-body);
    font-size: 1.15rem;
    color: rgba(255,255,255,0.7);
    margin-bottom: 2.5rem;
    line-height: 1.6;
  }
  .hero-cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    background: var(--clr-olive);
    color: #fff;
    padding: 1rem 2.5rem;
    font-family: var(--font-body);
    font-size: 0.95rem;
    font-weight: 500;
    letter-spacing: 1px;
    text-decoration: none;
    border-radius: 50px;
    transition: all 0.3s ease;
    text-transform: uppercase;
    &:hover {
      background: var(--clr-olive-dark);
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(0,0,0,0.3);
    }
  }

  /* Hero arrows */
  .hero-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.2);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    &:hover {
      background: rgba(255,255,255,0.25);
      transform: translateY(-50%) scale(1.1);
    }
  }
  .hero-arrow-left { left: 2rem; }
  .hero-arrow-right { right: 2rem; }

  /* Hero dots */
  .hero-dots {
    position: absolute;
    bottom: 7.5rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 5;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.875rem;
    padding: 0.625rem 1.25rem;
    border-radius: 24px;
    background: rgba(0,0,0,0.15);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1);
  }
  .hero-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255,255,255,0.35);
    border: none;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    padding: 0;
    flex-shrink: 0;
    &.active {
      width: 28px;
      border-radius: 4px;
      background: var(--clr-gold);
      box-shadow: 0 0 12px rgba(199, 161, 122, 0.6), 0 2px 8px rgba(0,0,0,0.2);
    }
    &:hover:not(.active) {
      background: rgba(255,255,255,0.65);
      transform: scale(1.15);
    }
  }

  /* Scroll indicator */
  .scroll-indicator {
    position: absolute;
    bottom: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.875rem;
    text-align: center;
    span {
      font-family: var(--font-body);
      font-size: 0.65rem;
      color: rgba(255,255,255,0.85);
      text-transform: uppercase;
      letter-spacing: 4px;
      font-weight: 600;
      text-shadow: 0 2px 4px rgba(0,0,0,0.3);
      white-space: nowrap;
    }
    .scroll-mouse {
      width: 26px;
      height: 40px;
      border: 2.5px solid rgba(255,255,255,0.7);
      border-radius: 13px;
      position: relative;
      background: rgba(0,0,0,0.1);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.1);
      .scroll-dot {
        width: 4px;
        height: 6px;
        background: #fff;
        border-radius: 2px;
        position: absolute;
        top: 7px;
        left: 50%;
        transform: translateX(-50%);
        animation: scrollMove 1.5s infinite;
        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      }
    }
  }

  /* ===== CATEGORIES SECTION ===== */
  .categories-section {
    padding: 6rem 0;
    background: var(--clr-warm-white);
    position: relative;
  }
  .categories-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 1.5rem;
  }
  .category-card {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    aspect-ratio: 3/4;
    box-shadow: var(--shadow-card);
  }
  .category-image-wrap {
    position: absolute;
    inset: 0;
    .category-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.7s ease;
    }
  }
  .category-card:hover .category-img {
    transform: scale(1.1);
  }
  .category-gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, transparent 100%);
    transition: opacity 0.3s ease;
  }
  .category-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1.5rem;
    z-index: 2;
  }
  .category-name {
    font-family: var(--font-display);
    font-size: 1.3rem;
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  .category-explore {
    font-family: var(--font-body);
    font-size: 0.8rem;
    color: var(--clr-gold);
    text-transform: uppercase;
    letter-spacing: 2px;
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.3s ease;
  }
  .category-card:hover .category-explore {
    opacity: 1;
    transform: translateY(0);
  }
  .category-corner {
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-top: 60px solid var(--clr-olive);
    border-left: 60px solid transparent;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 2;
  }
  .category-card:hover .category-corner {
    opacity: 0.8;
  }

  /* Category pagination dots - hidden on desktop */
  .category-dots {
    display: none;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }
  .category-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--clr-charcoal-light);
    opacity: 0.3;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  .category-dot.active {
    opacity: 1;
    background: var(--clr-olive);
    width: 24px;
    border-radius: 4px;
  }

  /* ===== BEST SELLERS ===== */
  .bestsellers-section {
    padding: 6rem 0;
    background: var(--clr-background);
    position: relative;
  }
  .bestsellers-bg-pattern {
    position: absolute;
    inset: 0;
    opacity: 0.03;
    background-image: radial-gradient(circle, var(--clr-olive) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
  }
  .products-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
    position: relative;
    z-index: 1;
  }
  .product-card {
    text-decoration: none;
    color: inherit;
    display: block;
  }
  .product-image-container {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    aspect-ratio: 3/4;
    background: var(--clr-beige);
  }
  .product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }
  .product-card:hover .product-image {
    transform: scale(1.08);
  }
  .product-hover-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0);
    transition: background 0.3s ease;
  }
  .product-card:hover .product-hover-overlay {
    background: rgba(0,0,0,0.08);
  }
  .product-badge {
    position: absolute;
    top: 1rem;
    left: 1rem;
    background: var(--clr-olive);
    color: #fff;
    padding: 0.35rem 0.85rem;
    border-radius: 20px;
    font-family: var(--font-body);
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .product-actions {
    position: absolute;
    top: 1rem;
    right: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    opacity: 0;
    transform: translateX(10px);
    transition: all 0.3s ease;
  }
  .product-card:hover .product-actions {
    opacity: 1;
    transform: translateX(0);
  }
  .product-action-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255,255,255,0.95);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--clr-charcoal);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.2s ease;
    &:hover {
      background: var(--clr-olive);
      color: #fff;
      transform: scale(1.1);
    }
  }
  .product-add-to-cart {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--clr-olive);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.85rem;
    font-family: var(--font-body);
    font-size: 0.8rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1px;
    transform: translateY(100%);
    transition: transform 0.3s ease;
  }
  .product-card:hover .product-add-to-cart {
    transform: translateY(0);
  }
  .product-info {
    padding: 1rem 0.25rem;
  }
  .product-rating {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-bottom: 0.5rem;
    .star {
      font-size: 0.85rem;
      color: #ddd;
      &.filled { color: var(--clr-gold); }
    }
    .rating-num {
      font-size: 0.75rem;
      color: var(--clr-charcoal-light);
      margin-left: 0.35rem;
    }
  }
  .product-name {
    font-family: var(--font-display);
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--clr-charcoal);
    margin-bottom: 0.35rem;
    line-height: 1.3;
  }
  .product-price {
    font-family: var(--font-body);
    font-size: 1rem;
    font-weight: 700;
    color: var(--clr-olive);
  }
  .products-cta {
    text-align: center;
    margin-top: 3rem;
    position: relative;
    z-index: 1;
  }
  .view-all-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.9rem 2.5rem;
    border: 2px solid var(--clr-olive);
    color: var(--clr-olive);
    text-decoration: none;
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    transition: all 0.3s ease;
    &:hover {
      background: var(--clr-olive);
      color: #fff;
      transform: translateY(-2px);
    }
  }

  /* ===== TRUST SECTION ===== */
  .trust-section {
    padding: 5rem 0;
    background: var(--clr-cream);
    position: relative;
    overflow: hidden;
  }
  .trust-bg-circle {
    position: absolute;
    border-radius: 50%;
    opacity: 0.08;
    pointer-events: none;
  }
  .trust-bg-1 {
    width: 400px;
    height: 400px;
    background: var(--clr-olive);
    top: -100px;
    right: -100px;
  }
  .trust-bg-2 {
    width: 300px;
    height: 300px;
    background: var(--clr-gold);
    bottom: -80px;
    left: -80px;
  }
  .trust-title {
    font-family: var(--font-display);
    font-size: clamp(1.6rem, 3vw, 2.2rem);
    color: var(--clr-charcoal);
    text-align: center;
    margin-bottom: 0.5rem;
    font-weight: 700;
  }
  .trust-subtitle {
    font-family: var(--font-body);
    font-size: 1rem;
    color: var(--clr-charcoal-light);
    text-align: center;
    margin-bottom: 2.5rem;
  }
  .trust-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
    position: relative;
    z-index: 1;
  }
  .trust-card {
    text-align: center;
    padding: 2.5rem 1.5rem;
    background: rgba(255,255,255,0.8);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(0,0,0,0.05);
    transition: all 0.3s ease;
    cursor: default;
    &:hover {
      box-shadow: var(--shadow-elevated);
    }
  }
  .trust-icon-wrap {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.25rem;
    &.olive {
      background: hsla(85, 30%, 32%, 0.12);
      color: var(--clr-olive);
    }
    &.gold {
      background: hsla(42, 65%, 55%, 0.15);
      color: var(--clr-gold);
    }
  }
  .trust-feature-title {
    font-family: var(--font-display);
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--clr-charcoal);
    margin-bottom: 0.5rem;
  }
  .trust-feature-desc {
    font-family: var(--font-body);
    font-size: 0.9rem;
    color: var(--clr-charcoal-light);
    line-height: 1.5;
  }

  /* ===== PROMO SECTION ===== */
  .promo-section {
    position: relative;
    padding: 6rem 0;
    overflow: hidden;
    text-align: center;
  }
  .promo-gradient-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--clr-olive-dark) 0%, var(--clr-charcoal) 100%);
  }
  .promo-shapes {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
  }
  .promo-particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: rgba(255,255,255,0.3);
    border-radius: 50%;
  }
  .promo-content {
    position: relative;
    z-index: 2;
  }
  .promo-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(255,255,255,0.1);
    color: var(--clr-gold);
    padding: 0.5rem 1.25rem;
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 1.5rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.1);
  }
  .promo-title {
    font-family: var(--font-display);
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    color: #fff;
    margin-bottom: 1rem;
  }
  .promo-highlight {
    color: var(--clr-gold);
    font-style: italic;
  }
  .promo-desc {
    font-family: var(--font-body);
    font-size: 1.1rem;
    color: rgba(255,255,255,0.8);
    max-width: 650px;
    margin: 0 auto 2.5rem;
    line-height: 1.7;
  }
  .promo-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 3rem;
    flex-wrap: wrap;
  }
  .promo-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    background: var(--clr-gold);
    color: var(--clr-charcoal);
    padding: 1rem 2.5rem;
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-decoration: none;
    transition: all 0.3s ease;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(0,0,0,0.3);
    }
  }
  .promo-btn-outline {
    display: inline-flex;
    align-items: center;
    padding: 1rem 2.5rem;
    border: 2px solid rgba(255,255,255,0.3);
    color: #fff;
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-decoration: none;
    transition: all 0.3s ease;
    &:hover {
      background: rgba(255,255,255,0.1);
      border-color: rgba(255,255,255,0.5);
    }
  }
  .promo-stats {
    display: flex;
    justify-content: center;
    gap: 4rem;
  }
  .promo-stat {
    text-align: center;
  }
  .stat-value {
    display: block;
    font-family: var(--font-display);
    font-size: 2rem;
    font-weight: 700;
    color: var(--clr-gold);
    margin-bottom: 0.25rem;
  }
  .stat-label {
    font-family: var(--font-body);
    font-size: 0.85rem;
    color: rgba(255,255,255,0.6);
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  /* ===== LIFESTYLE SECTION ===== */
  .lifestyle-section {
    padding: 6rem 0;
    background: var(--clr-warm-white);
  }
  .lifestyle-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
  }
  .lifestyle-image-wrap {
    position: relative;
  }
  .lifestyle-img-container {
    border-radius: 24px;
    overflow: hidden;
    box-shadow: var(--shadow-elevated);
  }
  .lifestyle-img {
    width: 100%;
    height: 500px;
    object-fit: cover;
    display: block;
  }
  .lifestyle-float-card {
    position: absolute;
    bottom: -2rem;
    right: -1.5rem;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(12px);
    padding: 1.5rem 2rem;
    border-radius: 16px;
    box-shadow: var(--shadow-elevated);
    z-index: 2;
  }
  .float-card-number {
    font-family: var(--font-display);
    font-size: 2rem;
    font-weight: 700;
    color: var(--clr-olive);
    display: block;
  }
  .float-card-text {
    font-family: var(--font-body);
    font-size: 0.85rem;
    color: var(--clr-charcoal-light);
    margin: 0;
  }
  .lifestyle-frame {
    position: absolute;
    top: -1rem;
    left: -1rem;
    right: 1rem;
    bottom: 1rem;
    border: 2px solid var(--clr-olive);
    border-radius: 24px;
    opacity: 0.2;
    z-index: -1;
  }
  .lifestyle-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .lifestyle-title {
    font-family: var(--font-display);
    font-size: clamp(1.8rem, 3vw, 2.5rem);
    color: var(--clr-charcoal);
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 0.5rem;
  }
  .text-olive { color: var(--clr-olive); }
  .text-gold { color: var(--clr-gold); }
  .lifestyle-desc {
    font-family: var(--font-body);
    font-size: 1rem;
    color: var(--clr-charcoal-light);
    line-height: 1.7;
    margin-bottom: 0.75rem;
  }
  .lifestyle-features {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
  .lifestyle-feature-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    .feature-line {
      width: 24px;
      height: 2px;
      background: var(--clr-olive);
      flex-shrink: 0;
    }
    p {
      font-family: var(--font-body);
      font-size: 0.95rem;
      color: var(--clr-charcoal);
      margin: 0;
    }
  }
  .lifestyle-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    background: var(--clr-olive);
    color: #fff;
    padding: 0.9rem 2rem;
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-decoration: none;
    transition: all 0.3s ease;
    &:hover {
      background: var(--clr-olive-dark);
      transform: translateY(-2px);
    }
  }

  /* ===== REVIEWS SECTION ===== */
  .reviews-section {
    padding: 6rem 0;
    background: var(--clr-background);
  }
  .reviews-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
  .review-card {
    background: #fff;
    border-radius: 20px;
    padding: 2rem;
    box-shadow: var(--shadow-card);
    transition: all 0.3s ease;
    &:hover {
      transform: translateY(-5px);
      box-shadow: var(--shadow-elevated);
    }
  }
  .review-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }
  .review-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--clr-olive), var(--clr-olive-light));
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-display);
    font-size: 1.2rem;
    font-weight: 700;
    flex-shrink: 0;
  }
  .review-name {
    font-family: var(--font-display);
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--clr-charcoal);
    margin: 0 0 0.25rem;
  }
  .review-stars {
    display: flex;
    gap: 2px;
    .star {
      font-size: 0.85rem;
      color: #ddd;
      &.filled { color: var(--clr-gold); }
    }
  }
  .review-comment {
    font-family: var(--font-body);
    font-size: 0.95rem;
    color: var(--clr-charcoal-light);
    line-height: 1.6;
    font-style: italic;
  }
  .add-review-container, .no-reviews {
    text-align: center;
    margin-top: 2.5rem;
  }
  .no-reviews p {
    font-family: var(--font-body);
    font-size: 1.05rem;
    color: var(--clr-charcoal-light);
    margin-bottom: 1.5rem;
  }
  .add-review-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.85rem 2.25rem;
    background: var(--clr-olive);
    color: #fff;
    border: none;
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
    &:hover {
      background: var(--clr-olive-dark);
      transform: translateY(-2px);
    }
  }

  /* ===== REVIEW MODAL ===== */
  .review-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }
  .review-modal {
    position: relative;
    background: #fff;
    border-radius: 20px;
    padding: 2.5rem;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }
  .modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: var(--clr-beige);
    font-size: 1.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--clr-charcoal);
    transition: background 0.2s ease;
    &:hover { background: var(--clr-beige-dark); }
  }
  .modal-title {
    font-family: var(--font-display);
    font-size: 1.6rem;
    font-weight: 700;
    color: var(--clr-charcoal);
    margin-bottom: 1.5rem;
  }
  .modal-field {
    margin-bottom: 1.25rem;
    label {
      display: block;
      font-family: var(--font-body);
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--clr-charcoal);
      margin-bottom: 0.5rem;
    }
  }
  .modal-stars {
    display: flex;
    gap: 0.5rem;
    .star-btn {
      background: none;
      border: none;
      font-size: 1.75rem;
      color: #ddd;
      cursor: pointer;
      transition: color 0.2s ease, transform 0.2s ease;
      padding: 0;
      &.active { color: var(--clr-gold); }
      &:hover { transform: scale(1.2); }
    }
  }
  .modal-textarea {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 2px solid var(--clr-beige);
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 0.95rem;
    resize: vertical;
    transition: border-color 0.2s ease;
    outline: none;
    &:focus { border-color: var(--clr-olive); }
  }
  .modal-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
  }
  .modal-submit {
    flex: 1;
    padding: 0.85rem;
    background: var(--clr-olive);
    color: #fff;
    border: none;
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover { background: var(--clr-olive-dark); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
  .modal-cancel {
    padding: 0.85rem 1.5rem;
    background: var(--clr-beige);
    color: var(--clr-charcoal);
    border: none;
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
    &:hover { background: var(--clr-beige-dark); }
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 1024px) {
    .categories-grid {
      grid-template-columns: repeat(3, 1fr);
    }
    .products-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .trust-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .lifestyle-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    .lifestyle-img {
      height: 380px;
    }
    .lifestyle-float-card {
      bottom: -1rem;
      right: 1rem;
    }
  }

  @media (max-width: 768px) {
    /* Hero mobile improvements */
    .hero-section {
      margin-top: -88px;
      height: calc(100svh + 88px);
      min-height: calc(100vh + 88px);
    }
    .hero-title {
      font-size: clamp(2.5rem, 10vw, 3.5rem);
    }
    .hero-subtitle {
      font-size: clamp(1rem, 4vw, 1.4rem);
    }
    .hero-description {
      font-size: 0.9rem;
      max-width: 320px;
      margin-left: auto;
      margin-right: auto;
    }
    .hero-decorative-line {
      gap: 0.75rem;
      margin-bottom: 1rem;
    }
    .hero-decorative-line .line-bar {
      width: 30px;
    }
    .hero-decorative-line .line-text {
      font-size: 0.7rem;
      letter-spacing: 2px;
    }
    .hero-arrow {
      width: 44px;
      height: 44px;
      padding: 0.5rem;
    }
    .hero-arrow-left { left: 0.5rem; }
    .hero-arrow-right { right: 0.5rem; }
    .hero-arrow svg {
      width: 20px;
      height: 20px;
    }
    .hero-dots {
      bottom: 5rem;
      gap: 0.75rem;
      padding: 0.5rem 1rem;
    }
    .hero-dot {
      width: 10px;
      height: 10px;
    }
    .hero-dot.active {
      width: 28px;
    }
    .scroll-indicator {
      bottom: 1.5rem;
    }
    .scroll-indicator span {
      font-size: 0.6rem;
      letter-spacing: 2px;
    }
    .scroll-indicator .scroll-mouse {
      width: 22px;
      height: 34px;
      border-width: 2px;
    }
    .scroll-indicator .scroll-dot {
      width: 3px;
      height: 5px;
      top: 6px;
    }
    .hero-cta-btn {
      padding: 0.85rem 2rem;
      font-size: 0.85rem;
    }
    .hero-particles .particle {
      width: 4px;
      height: 4px;
    }
    .hero-particles .particle:nth-child(n+5) {
      display: none;
    }

    /* Categories mobile - horizontal scroll */
    .categories-section {
      padding: 4rem 0;
    }
    .categories-grid {
      display: flex;
      overflow-x: auto;
      gap: 0.75rem;
      padding-bottom: 1rem;
      margin: 0 -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }
    .category-card {
      flex: 0 0 140px;
      width: 140px;
      aspect-ratio: 3/4;
      scroll-snap-align: center;
    }
    .category-name {
      font-size: 1rem;
    }
    .category-content {
      padding: 1rem;
    }
    .category-explore {
      opacity: 1;
      transform: none;
      font-size: 0.65rem;
    }
    .category-corner {
      display: none;
    }

    /* Category dots - visible on mobile */
    .category-dots {
      display: flex;
    }

    /* Products grid mobile */
    .bestsellers-section {
      padding: 4rem 0;
    }
    .products-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }
    .product-image-container {
      border-radius: 12px;
    }
    .product-badge {
      font-size: 0.6rem;
      padding: 0.25rem 0.6rem;
      top: 0.5rem;
      left: 0.5rem;
    }
    .product-actions {
      opacity: 1;
      transform: none;
      top: 0.5rem;
      right: 0.5rem;
    }
    .product-action-btn {
      width: 30px;
      height: 30px;
    }
    .product-action-btn svg {
      width: 14px;
      height: 14px;
    }
    .product-add-to-cart {
      padding: 0.65rem;
      font-size: 0.7rem;
      transform: none;
    }
    .product-info {
      padding: 0.75rem 0.125rem 0;
    }
    .product-name {
      font-size: 0.85rem;
    }
    .product-price {
      font-size: 0.9rem;
    }
    .product-rating .star {
      font-size: 0.7rem;
    }
    .product-rating .rating-num {
      font-size: 0.65rem;
    }

    /* Trust section mobile */
    .trust-section {
      padding: 3rem 0;
    }
    .trust-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }
    .trust-card {
      padding: 1.5rem 1rem;
      border-radius: 16px;
    }
    .trust-icon-wrap {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      margin-bottom: 1rem;
    }
    .trust-icon-wrap svg {
      width: 22px;
      height: 22px;
    }
    .trust-feature-title {
      font-size: 0.85rem;
    }
    .trust-feature-desc {
      font-size: 0.75rem;
      display: none;
    }
    .trust-title {
      font-size: 1.4rem;
    }
    .trust-subtitle {
      font-size: 0.85rem;
      margin-bottom: 1.5rem;
    }
    .trust-bg-1 {
      width: 200px;
      height: 200px;
      top: -50px;
      right: -50px;
    }
    .trust-bg-2 {
      width: 150px;
      height: 150px;
      bottom: -40px;
      left: -40px;
    }

    /* Promo section mobile */
    .promo-section {
      padding: 4rem 0;
    }
    .promo-badge {
      font-size: 0.65rem;
      padding: 0.4rem 0.85rem;
      gap: 0.35rem;
    }
    .promo-badge svg {
      width: 12px;
      height: 12px;
    }
    .promo-title {
      font-size: clamp(1.75rem, 8vw, 2.5rem);
    }
    .promo-desc {
      font-size: 0.9rem;
      max-width: 320px;
    }
    .promo-buttons {
      flex-direction: column;
      gap: 0.75rem;
      padding: 0 1rem;
    }
    .promo-btn-primary,
    .promo-btn-outline {
      padding: 0.9rem 2rem;
      font-size: 0.85rem;
      justify-content: center;
    }
    .promo-stats {
      gap: 1.5rem;
    }
    .stat-value {
      font-size: 1.4rem;
    }
    .stat-label {
      font-size: 0.7rem;
    }

    /* Lifestyle section mobile */
    .lifestyle-section {
      padding: 4rem 0;
    }
    .lifestyle-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    .lifestyle-img {
      height: 280px;
      border-radius: 16px;
    }
    .lifestyle-img-container {
      border-radius: 16px;
    }
    .lifestyle-float-card {
      bottom: -1rem;
      right: -0.5rem;
      padding: 1rem 1.25rem;
      max-width: 140px;
    }
    .float-card-number {
      font-size: 1.5rem;
    }
    .float-card-text {
      font-size: 0.7rem;
    }
    .lifestyle-frame {
      display: none;
    }
    .lifestyle-title {
      font-size: clamp(1.5rem, 6vw, 2rem);
    }
    .lifestyle-desc {
      font-size: 0.9rem;
    }
    .lifestyle-feature-item .feature-line {
      width: 20px;
    }
    .lifestyle-feature-item p {
      font-size: 0.8rem;
    }
    .lifestyle-cta {
      padding: 0.85rem 1.75rem;
      font-size: 0.85rem;
    }

    /* Reviews section mobile */
    .reviews-section {
      padding: 4rem 0;
    }
    .reviews-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
    .review-card {
      padding: 1.5rem;
      border-radius: 16px;
    }
    .review-avatar {
      width: 40px;
      height: 40px;
      font-size: 1rem;
    }
    .review-name {
      font-size: 0.95rem;
    }
    .review-comment {
      font-size: 0.85rem;
    }

    /* Section headers mobile */
    .section-header {
      margin-bottom: 2.5rem;
    }
    .section-label {
      font-size: 0.75rem;
      letter-spacing: 2px;
    }
    .section-title-main {
      font-size: clamp(1.75rem, 6vw, 2.5rem);
    }
    .section-divider {
      width: 60px;
      height: 2px;
    }
    .section-subtitle {
      font-size: 0.9rem;
    }
    .section-container {
      padding: 0 1rem;
    }
  }

  @media (max-width: 480px) {
    /* Extra small mobile tweaks */
    .hero-title {
      font-size: clamp(2rem, 12vw, 3rem);
    }
    .category-card {
      flex: 0 0 130px;
      width: 130px;
    }
    .products-grid {
      gap: 0.5rem;
    }
    .product-name {
      font-size: 0.75rem;
      line-height: 1.2;
    }
    .product-price {
      font-size: 0.8rem;
    }
    .product-rating {
      margin-bottom: 0.25rem;
    }
    .product-rating .star {
      font-size: 0.6rem;
    }
    .trust-grid {
      gap: 0.75rem;
    }
    .trust-card {
      padding: 1.25rem 0.75rem;
    }
    .promo-stats {
      gap: 1rem;
    }
    .lifestyle-float-card {
      display: none;
    }
    .review-modal {
      padding: 1.5rem;
      margin: 0.5rem;
    }
    .modal-title {
      font-size: 1.3rem;
    }
  }

  /* Touch optimizations */
  @media (hover: none) and (pointer: coarse) {
    .category-card,
    .product-card,
    .trust-card,
    .review-card,
    .hero-arrow,
    .hero-dot,
    .promo-btn-primary,
    .promo-btn-outline,
    .lifestyle-cta,
    .hero-cta-btn,
    .view-all-btn,
    .add-review-btn {
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }
    .product-actions {
      opacity: 1;
      transform: none;
    }
    .product-add-to-cart {
      transform: none;
    }
    .category-explore {
      opacity: 1;
      transform: none;
    }
  }
`;
